import React, { useState, useMemo, useCallback } from "react";
import { DealerUsersProps } from "store/modules/DealerUsers/types.store";
import { useAppDispatch } from "store/hooks";
import { handleGetDealerDriversRequest } from "store/modules/DealerUsers/thunk.store";
import { handleUpdateOrderRequest } from "store/modules/Orders/thunk.store";
import { useSignalR } from "context/SignalRContext";
import { useAuth } from "context/AuthContext";
import CardOrderTracking from "./components/CardOrderTracking";
import { OrderUpdateActions } from "store/modules/Orders/types.store";
import CancelOrderModal from "pages/ManageOrders/components/CancelOrderModal";
import getOrderModalsProps from "pages/ManageOrders/getOrderModalsProps";
import { SlideModal, TabsNavigation } from "ctech-react-components";
import { Autocomplete, Box, Grid, TextField, Typography } from "@mui/material";
import * as Styled from "./styles";
import { useTranslation } from "react-i18next";
import CardOrderDetails from "./components/CardOrderDetails";
import {
  LocalShipping as LocalShippingIcon,
  PropaneTank as PropaneTankIcon,
  CheckCircleOutline as CheckCircleIcon,
} from "@mui/icons-material";

type Anchor = "top" | "left" | "bottom" | "right";

const useMarkesModalOrderAll = () => {
  const { t } = useTranslation();
  const { ordersSignal } = useSignalR();
  const { dealerId } = useAuth();
  const dispatch = useAppDispatch();
  const [state, setState] = useState(false);
  const [currentTab, setCurrentTab] = useState("1");
  const [allIUsers, setAllIUsers] = useState<DealerUsersProps[]>();
  const [driveSelected, setDriveSelected] = useState<{
    label: string;
    id: string;
  } | null>(null);

  const [pinSelected, setPinSelected] = useState<{
    type: string;
    id: string;
  }>();

  const [markerAndDrawLine, setMarkerAndDrawLine] = useState<any>();
  const [ordersSelected, setOrdersSelected] = useState<number | null>(null);
  const [typeDetails, setTypeDetails] = useState({
    currentTab: 0,
    type: "",
  });
  const [modalProps, setModalProps] = useState<{
    orderId: string;
    consumerId: string;
    type: OrderUpdateActions;
  } | null>(null);
  const [addDriver, setAddDriver] = useState(false);

  const typeOrders = useCallback(
    (type: string) => {
      const ordersSignalFilter =
        ordersSignal?.filter((x: any) => x.dealerState === type) || [];

      if (driveSelected) {
        return ordersSignalFilter.filter(
          (order: any) => order.driver?.id === driveSelected.id,
        );
      }

      return ordersSignalFilter;
    },
    [ordersSignal, driveSelected],
  );

  const countSoldItems = () => {
    let soldItems = 0;

    typeOrders("Finalized").forEach((order) =>
      order.items.forEach((orderItem) => {
        soldItems += orderItem.quantity || 0;
      }),
    );

    return soldItems;
  };

  const handleChangeTab = async (_: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
  };

  const handledUsers = async () => {
    const response = await dispatch(
      handleGetDealerDriversRequest({
        dealerId: dealerId || "",
        limit: 100,
      }),
    ).unwrap();

    setAllIUsers(response.data);
  };

  const IUsers = useMemo(() => {
    return allIUsers?.map((x: DealerUsersProps) => ({
      label: `${x?.name ?? ""} ${x?.surname ?? ""}`.trim(),
      id: x?.id,
    }));
  }, [allIUsers]);

  const handleOrderUpdate = useCallback(
    (type: OrderUpdateActions, orderId: string, consumerId: string) => {
      setModalProps({ orderId, type, consumerId });
    },
    [],
  );

  const CardsAccordionNew = useMemo(() => {
    if (currentTab === "1" && typeOrders("New").length > 0) {
      return (
        typeOrders("New").length > 0 &&
        typeOrders("New").map((order: any, index: any) => (
          <CardOrderTracking
            pinSelected={
              pinSelected?.type === "order" && order.id === pinSelected.id
            }
            ordersSelected={typeDetails.type === "New" ? ordersSelected : null}
            indexSelected={index}
            key={order.id}
            data={order}
            handleOrderAccept={handleOrderUpdate}
            handleOrderUpdate={(id, consumerId, select) => {
              setTypeDetails({
                type: "New",
                currentTab: 1,
              });

              if (select) {
                setOrdersSelected(index);
              }

              return handleOrderUpdate;
            }}
          />
        ))
      );
    }

    if (currentTab === "1" && typeOrders("New").length === 0) {
      return (
        <Typography
          variant="caption"
          textAlign="start"
          fontSize={15}
          sx={{
            marginTop: 15,
          }}
        >
          Você não tem pedidos em andamento
        </Typography>
      );
    }
    return "";
  }, [
    currentTab,
    typeOrders,
    pinSelected?.type,
    pinSelected?.id,
    typeDetails.type,
    ordersSelected,
    handleOrderUpdate,
  ]);

  const CardsAccordionInProgress = useMemo(() => {
    if (currentTab === "2" && typeOrders("InProgress").length > 0) {
      return typeOrders("InProgress").map((order: any, index: any) => (
        <CardOrderTracking
          pinSelected={
            pinSelected?.type === "order" && order.id === pinSelected.id
          }
          ordersSelected={null}
          indexSelected={index}
          key={order.id}
          data={order}
          handleOrderUpdate={(id, consumerId, select) => {
            setTypeDetails({
              type: "InProgress",
              currentTab: 1,
            });

            if (select) {
              setOrdersSelected(index);
            }

            return handleOrderUpdate;
          }}
        />
      ));
    }

    if (currentTab === "2" && typeOrders("InProgress").length === 0) {
      return (
        <Typography
          variant="caption"
          textAlign="start"
          fontSize={15}
          sx={{
            marginTop: 15,
          }}
        >
          Você não tem pedidos em andamento
        </Typography>
      );
    }
    return "";
  }, [
    currentTab,
    typeOrders,
    pinSelected?.type,
    pinSelected?.id,
    handleOrderUpdate,
  ]);

  const toggleDrawer =
    (anchor: Anchor, open: boolean) =>
    (event: React.KeyboardEvent | React.MouseEvent) => {
      if (!open) {
        setPinSelected(undefined);
        setOrdersSelected(null);
        setMarkerAndDrawLine(null);
        setCurrentTab("1");
        setDriveSelected(null);
      }

      if (
        event &&
        event.type === "keydown" &&
        ((event as React.KeyboardEvent).key === "Tab" ||
          (event as React.KeyboardEvent).key === "Shift")
      ) {
        return;
      }

      setState(open);
    };

  const list = (anchor: Anchor) => (
    <Box
      sx={{
        width: anchor === "top" || anchor === "bottom" ? "auto" : 500,
        height: "100vh",
      }}
      role="presentation"
      onKeyDown={toggleDrawer(anchor, false)}
    >
      <Styled.Content>
        {ordersSelected !== null ? (
          <CardOrderDetails
            handleBack={() => {
              setOrdersSelected(null);
              setMarkerAndDrawLine(null);
            }}
            currentTab={currentTab}
            data={typeOrders(typeDetails.type)[ordersSelected]}
            handleOrderUpdate={handleOrderUpdate}
          />
        ) : (
          <>
            <Styled.ContainerTab>
              <TabsNavigation
                selectedTab={currentTab}
                tabs={[
                  {
                    label: `${t("newRequests").toUpperCase()} (${
                      typeOrders("New").length
                    })`,
                    value: "1",
                  },
                  {
                    label: `${t("ordersInProgress").toUpperCase()} (${
                      typeOrders("InProgress").length
                    })`,
                    value: "2",
                  },
                ]}
                onChangeTab={handleChangeTab}
                spaceAroundTabs
                tabStyle={{
                  paddingRight: 10,
                  justifyContent: "space-between",
                }}
              />
            </Styled.ContainerTab>

            <Styled.ContainerDriver>
              <Typography
                variant="caption"
                textAlign="start"
                color="text.secondary"
              >
                Selecione um entregador para filtrar os pedidos
              </Typography>
              {!addDriver ? (
                <Styled.SelectDriver>
                  <Styled.ContainerDriverSelected>
                    <LocalShippingIcon fontSize="small" color="inherit" />
                    <Typography variant="subtitle2">
                      {driveSelected ? driveSelected.label : "Todos"}
                    </Typography>
                  </Styled.ContainerDriverSelected>

                  <Styled.Button
                    type="button"
                    onClick={() => {
                      setAddDriver(true);
                      handledUsers();
                    }}
                  >
                    <Styled.ButtonAccept color="inherit">
                      {driveSelected ? "TROCAR" : "SELECIONAR ENTREGADOR"}
                    </Styled.ButtonAccept>
                  </Styled.Button>
                </Styled.SelectDriver>
              ) : (
                <Styled.ContainerAutoComplete>
                  <Autocomplete
                    disablePortal
                    id="combo-box-demo"
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    options={IUsers || []}
                    size="small"
                    defaultValue={driveSelected?.label}
                    onChange={(_, e) => setDriveSelected(e as any)}
                    renderInput={(params) => (
                      <TextField {...params} label={t("selectDriver")} />
                    )}
                    sx={{
                      width: 200,
                    }}
                  />

                  <Grid>
                    <Styled.ContainerButtonsDrive>
                      <Styled.ButtonBackDrive
                        type="button"
                        onClick={() => setAddDriver(false)}
                      >
                        <Typography
                          variant="button"
                          textAlign="start"
                          onClick={() => setDriveSelected(null)}
                        >
                          Sair
                        </Typography>
                      </Styled.ButtonBackDrive>
                      <Styled.ButtonApplyDrive
                        type="button"
                        onClick={() => setAddDriver(false)}
                      >
                        <Typography variant="button" textAlign="start">
                          Aplicar
                        </Typography>
                      </Styled.ButtonApplyDrive>
                    </Styled.ContainerButtonsDrive>
                  </Grid>
                </Styled.ContainerAutoComplete>
              )}

              <Styled.ContainerOrdersInfo>
                <Styled.OrdersInfo>
                  <CheckCircleIcon color="inherit" fontSize="small" />
                  <Typography variant="subtitle2">
                    Pedidos Finalizados
                  </Typography>

                  <Typography variant="h6" color="#2E7D32">
                    {typeOrders("Finalized").length}
                  </Typography>
                </Styled.OrdersInfo>
                <Styled.OrdersInfo>
                  <PropaneTankIcon color="inherit" fontSize="small" />
                  <Typography variant="subtitle2">
                    Botijões entregues
                  </Typography>
                  <Typography variant="h6" color="#2E7D32">
                    {countSoldItems()}
                  </Typography>
                </Styled.OrdersInfo>
              </Styled.ContainerOrdersInfo>
            </Styled.ContainerDriver>

            <Styled.ContainerCards>
              {CardsAccordionInProgress}
              {CardsAccordionNew}
            </Styled.ContainerCards>
          </>
        )}
      </Styled.Content>
    </Box>
  );

  const modalRender = useMemo(() => {
    if (!modalProps) {
      // eslint-disable-next-line react/jsx-no-useless-fragment
      return <></>;
    }

    const onClose = () => setModalProps(null);
    const onConfirm = async (stateOrder?: unknown) => {
      await dispatch(
        handleUpdateOrderRequest({
          dealerId,
          type: modalProps.type,
          orderId: modalProps.orderId,
          body: stateOrder,
          consumerId: modalProps.consumerId,
        }),
      )
        .unwrap()
        .finally();

      setOrdersSelected(null);
      setMarkerAndDrawLine(null);
      setTypeDetails({
        type: "",
        currentTab: 0,
      });

      onClose();
    };

    const isCancelType = modalProps.type === "cancel";
    const isDenyType = modalProps.type === "deny";
    if (isCancelType) {
      return (
        <CancelOrderModal
          labelType="CANCEL"
          isOpen={!!modalProps}
          onClose={onClose}
          onConfirm={onConfirm}
        />
      );
    }
    if (isDenyType) {
      return (
        <CancelOrderModal
          labelType="DENY"
          isOpen={!!modalProps}
          onClose={onClose}
          onConfirm={onConfirm}
        />
      );
    }

    return (
      <SlideModal
        {...getOrderModalsProps(modalProps.type)}
        isOpen={!!modalProps}
        onClose={onClose}
        onConfirm={onConfirm}
      />
    );
  }, [
    dealerId,
    setModalProps,
    setOrdersSelected,
    setTypeDetails,
    dispatch,
    modalProps,
  ]);

  return {
    modalRender,
    list,
    toggleDrawer,
    state,
    setState,
    currentTab,
    setCurrentTab,
    pinSelected,
    setPinSelected,
    setDriveSelected,
    markerAndDrawLine,
    setMarkerAndDrawLine,
  };
};

export default useMarkesModalOrderAll;
